import type { ReactNode } from "react";

import { useCraneT } from "../lib/i18n";
import { cn } from "../lib/utils";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

/** Modale administracyjne. Teksty domyślne pochodzą z adaptera `useCraneT`. */

export interface ConfirmModalProps {
  open: boolean;
  /** Domyślnie pytanie o usunięcie — najczęstszy przypadek na listach CRUD. */
  title?: string;
  /** Domyślnie ostrzeżenie o nieodwracalności; `null` ukrywa opis. */
  description?: string | null;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Blokuje oba przyciski i pokazuje spinner w przycisku potwierdzenia (Button.loading)
   *  na czas operacji. Bez tego użytkownik może kliknąć „Usuń" drugi raz, zanim pierwsze
   *  żądanie wróci. */
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

/** Potwierdzenie operacji z domyślną treścią dla usuwania. Inne zastosowania,
 *  np. guard niezapisanych zmian, przekazują własny tytuł i opis. */
const ConfirmModal = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  danger = true,
  isLoading = false,
  onConfirm,
  onCancel,
  children,
}: ConfirmModalProps) => {
  const t = useCraneT();

  const resolvedTitle = title ?? t("common.deleteQuestion");
  const resolvedDescription = description === null ? undefined : (description ?? t("common.deleteConfirmation"));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className={cn("text-crane-base", danger && "text-crane-status-alarm")}>
            {resolvedTitle}
          </DialogTitle>
          {resolvedDescription ? <DialogDescription>{resolvedDescription}</DialogDescription> : null}
        </DialogHeader>

        {children}

        <DialogFooter className="gap-2">
          <Button variant="ghost" disabled={isLoading} onClick={onCancel}>
            {cancelLabel ?? t("commonActions.cancel")}
          </Button>
          <Button variant={danger ? "destructive" : "default"} loading={isLoading} onClick={onConfirm}>
            {confirmLabel ?? t("commonActions.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export interface WarningModalProps {
  open: boolean;
  title?: string;
  description: string;
  items: string[];
  onClose: () => void;
}

/** Komunikat ostrzegawczy z opcjonalną listą szczegółów. */
export const WarningModal = ({ open, title, description, items, onClose }: WarningModalProps) => {
  const t = useCraneT();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-crane-base text-crane-status-attention">
            {title ?? t("common.warning")}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {items.length > 0 && (
          <ul className="flex flex-col gap-1.5 text-sm text-crane-foreground">
            {items.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <span className="size-2.5 shrink-0 rounded-crane-sm bg-crane-status-alarm" aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button onClick={onClose}>{t("common.ok")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ConfirmModal };
